import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getGeneralAPIRequest, getBessStatusBySystemId } from '../../utils/apiCommonMethods';
import { allowedSolarOnlySystemIds } from '../../apiConfig';

// Solar-only systems only report solar output (kW); battery systems report SOC, power and mode
const solarOnlyPoints = ['kw'];
const batteryPoints = ['soc', 'power', 'mode'];

export const fetchBessStatusBySystemId = createAsyncThunk(
  'bess/fetchStatus',
  async ({ msSessionInfo, systemIds }, { rejectWithValue }) => {
    const axiosSession = getGeneralAPIRequest(msSessionInfo);
    const solarSystemIds = systemIds.filter((id) => allowedSolarOnlySystemIds.has(id));
    const batterySystemIds = systemIds.filter((id) => !allowedSolarOnlySystemIds.has(id));

    const requests = [];
    if (solarSystemIds.length > 0) {
      requests.push(getBessStatusBySystemId(axiosSession, solarSystemIds, solarOnlyPoints));
    }
    if (batterySystemIds.length > 0) {
      requests.push(getBessStatusBySystemId(axiosSession, batterySystemIds, batteryPoints));
    }
    const results = await Promise.all(requests);

    // Keep whichever requests succeeded; only reject if every request failed
    const successful = results.filter((result) => !result.error);
    if (successful.length === 0) {
      return rejectWithValue(results[0]?.error || 'Offline');
    }

    return successful.flatMap((result) => result.data);
  }
);

const bessSlice = createSlice({
  name: 'bess',
  initialState: {
    loading: false,
    error: null,
    data: []
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBessStatusBySystemId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBessStatusBySystemId.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;

        const newEntries = action.payload;

        // Filter out existing entries based on systemId
        const filtered = state.data.filter(
          existing => !newEntries.some(newItem => newItem.systemId === existing.systemId)
        );

        state.data = [...filtered, ...newEntries];
      })
      .addCase(fetchBessStatusBySystemId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Offline';
      });
  }
});

export default bessSlice.reducer;
